import React, { useCallback, useContext, useMemo, useState } from 'react';
import { Button, Typography } from '@mui/material';
import Dialog from '@mui/material/Dialog';
import brazilStyles from './brazil.styles';
import SeparationWorklistPage from '../Common/SeparationWorklist/SeparationWorklistPage';
import SECTION_SEPARATION_QC_OPTIONS from '../Common/SeparationWorklist/sectionQcStatusOptions';
import { SectionMappingMode } from '../Common/SectionMapping/types';
import { SeparationWorklistConfig, WorklistRow } from '../Common/SeparationWorklist/types';
import BrazilSectionMappingDialog from './BrazilSectionMappingDialog';
import BrazilSectionSeparationDialog from './BrazilSectionSeparationDialog';
import { triggerBrazilDag } from '../../api/brazil';
import Actions from '../../store/actions';
import Store from '../../store';

const BRAZIL_API_BASE = '/brazil';

const LabelToSectionListing = () => {
  const { dispatch } = useContext<any>(Store);
  const [triggeringDag, setTriggeringDag] = useState(false);
  const [sectionSeparationOpen, setSectionSeparationOpen] = useState(false);
  const [mappingOpen, setMappingOpen] = useState(false);
  const [mappingMode, setMappingMode] = useState<SectionMappingMode>('section');
  const [selectedRow, setSelectedRow] = useState<WorklistRow | null>(null);
  const [worklistRefreshToken, setWorklistRefreshToken] = useState(0);
  const [dialogBusy, setDialogBusy] = useState(false);
  const [mappingDialogBusy, setMappingDialogBusy] = useState(false);

  const handleOpenSectionSeparation = useCallback((row: WorklistRow) => {
    setSelectedRow(row);
    setSectionSeparationOpen(true);
  }, []);

  const handleOpenSectionPageMapping = useCallback((row: WorklistRow) => {
    setSelectedRow(row);
    setMappingMode('section');
    setMappingOpen(true);
  }, []);

  const handleOpenOriginalPageMapping = useCallback((row: WorklistRow) => {
    setSelectedRow(row);
    setMappingMode('original');
    setMappingOpen(true);
  }, []);

  const handleCloseSectionSeparation = useCallback(() => {
    setSectionSeparationOpen(false);
    setSelectedRow(null);
    setDialogBusy(false);
  }, []);

  const handleCloseMapping = useCallback(() => {
    setMappingOpen(false);
    setSelectedRow(null);
    setMappingDialogBusy(false);
  }, []);

  const handleMappingDialogClose = useCallback(() => {
    if (mappingDialogBusy) {
      return;
    }
    handleCloseMapping();
  }, [handleCloseMapping, mappingDialogBusy]);

  const handleSectionSeparationDialogClose = useCallback(() => {
    if (dialogBusy) {
      return;
    }
    handleCloseSectionSeparation();
  }, [dialogBusy, handleCloseSectionSeparation]);

  const handleSaved = useCallback(() => {
    setWorklistRefreshToken(current => current + 1);
  }, []);

  const handleTriggerDag = useCallback(async () => {
    setTriggeringDag(true);
    try {
      const response = await triggerBrazilDag();
      const message =
        response?.data?.Success ?? response?.data?.message ?? 'DAG triggered successfully';
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message, color: 'success' }
      });
    } catch (error: unknown) {
      const message = error instanceof Error ? error.message : 'Failed to trigger DAG';
      await dispatch({
        type: Actions.SET_ALERT,
        value: { status: true, message }
      });
    } finally {
      setTriggeringDag(false);
    }
  }, [dispatch]);

  const config = useMemo<SeparationWorklistConfig>(
    () => ({
      apiBasePath: BRAZIL_API_BASE,
      stage: 'section',
      searchLabel: 'Registration / Process / Submission ID',
      qcStatusOptions: SECTION_SEPARATION_QC_OPTIONS,
      rowActions: [
        {
          label: 'Section Separation',
          getVariant: row => (row.section_separation_done ? 'contained' : 'outlined'),
          onAction: handleOpenSectionSeparation
        },
        {
          label: 'Section Page Mapping',
          getVariant: row => (row.section_page_mapping_done ? 'contained' : 'outlined'),
          isDisabled: row => !row.section_separation_done,
          onAction: handleOpenSectionPageMapping
        },
        {
          label: 'Original Page Mapping',
          getVariant: row => (row.original_page_mapping_done ? 'contained' : 'outlined'),
          isDisabled: row => !row.section_separation_done,
          onAction: handleOpenOriginalPageMapping
        }
      ]
    }),
    [handleOpenOriginalPageMapping, handleOpenSectionPageMapping, handleOpenSectionSeparation]
  );

  return (
    <>
      <SeparationWorklistPage
        config={config}
        refreshToken={worklistRefreshToken}
        headerActions={
          <Button
            variant='outlined'
            disabled={triggeringDag}
            sx={brazilStyles.actionButton}
            onClick={handleTriggerDag}>
            <Typography>{triggeringDag ? 'Triggering…' : 'Trigger Post-Curation DAG'}</Typography>
          </Button>
        }
      />
      <Dialog
        open={sectionSeparationOpen}
        onClose={handleSectionSeparationDialogClose}
        disableEscapeKeyDown={dialogBusy}
        sx={brazilStyles.backdropDialog}
        maxWidth='xl'
        fullWidth>
        {selectedRow && sectionSeparationOpen ? (
          <BrazilSectionSeparationDialog
            documentId={selectedRow.document_id}
            registrationNumber={selectedRow.registration_number}
            onClose={handleCloseSectionSeparation}
            onSaved={handleSaved}
            onBusyChange={setDialogBusy}
          />
        ) : null}
      </Dialog>
      <Dialog
        open={mappingOpen}
        onClose={handleMappingDialogClose}
        disableEscapeKeyDown={mappingDialogBusy}
        sx={brazilStyles.backdropDialog}
        maxWidth='xl'
        fullWidth>
        {selectedRow && mappingOpen ? (
          <BrazilSectionMappingDialog
            documentId={selectedRow.document_id}
            mode={mappingMode}
            registrationNumber={selectedRow.registration_number}
            onClose={handleCloseMapping}
            onSaved={handleSaved}
            onBusyChange={setMappingDialogBusy}
          />
        ) : null}
      </Dialog>
    </>
  );
};

export default LabelToSectionListing;
